import type { ImgHTMLAttributes } from "react";

const DEFAULT_WIDTHS = [480, 768, 1200, 1600] as const;
const TRANSFORMABLE_MEDIA = /\.(avif|jpe?g|png|webp)$/i;

function appendTransform(src: string, width: number, format: "avif" | "webp") {
  const separator = src.includes("?") ? "&" : "?";
  return `${src}${separator}w=${width}&format=${format}`;
}

function isManagedRaster(src: string) {
  const isManagedOrigin = src.startsWith("/media/") || src.startsWith("https://integrated.social/media/");
  return isManagedOrigin && TRANSFORMABLE_MEDIA.test(src.split("?")[0] ?? "");
}

export interface ResponsiveImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "width" | "height"> {
  src: string;
  alt: string;
  width: number;
  height: number;
  /** The rendered slot sizes for browser source selection. */
  sizes?: string;
  /** Defaults cover common card and hero slots without upscaling the source. */
  widths?: readonly number[];
  /** Avoids competing with above-the-fold LCP images. */
  priority?: boolean;
}

/**
 * Keeps durable `/media/` source URLs as the fallback and social-card source,
 * while allowing browser-facing content images to select compact AVIF/WebP
 * variants from the same origin. Non-managed, SVG and animated images retain
 * their existing URL so external product and affiliate imagery is untouched.
 */
export function ResponsiveImage({
  src,
  alt,
  width,
  height,
  sizes,
  widths = DEFAULT_WIDTHS,
  priority = false,
  loading,
  decoding = "async",
  fetchPriority,
  ...props
}: ResponsiveImageProps) {
  const shouldTransform = isManagedRaster(src);
  const resolvedLoading = priority ? "eager" : (loading ?? "lazy");
  const resolvedDecoding = priority ? "sync" : decoding;
  const resolvedFetchPriority = priority ? "high" : fetchPriority;
  const srcSet = shouldTransform
    ? widths.map((candidateWidth) => `${appendTransform(src, candidateWidth, "webp")} ${candidateWidth}w`).join(", ")
    : undefined;
  const avifSrcSet = shouldTransform
    ? widths.map((candidateWidth) => `${appendTransform(src, candidateWidth, "avif")} ${candidateWidth}w`).join(", ")
    : undefined;

  if (!shouldTransform) {
    return (
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={resolvedLoading}
        decoding={resolvedDecoding}
        fetchPriority={resolvedFetchPriority}
        sizes={sizes}
        {...props}
      />
    );
  }

  return (
    <picture className="block">
      <source type="image/avif" srcSet={avifSrcSet} sizes={sizes} />
      <source type="image/webp" srcSet={srcSet} sizes={sizes} />
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={resolvedLoading}
        decoding={resolvedDecoding}
        fetchPriority={resolvedFetchPriority}
        sizes={sizes}
        {...props}
      />
    </picture>
  );
}

export const responsiveMediaUrl = appendTransform;
